import { AionCta } from "@/components/site/AionCta";
import { OG_IMAGES } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Books — Build Don't Talk: Summary, Price & Where to Buy";
const DESC =
  "A complete guide to Raj Shamani's books. Build Don't Talk summary, chapter takeaways, who it's for, price in India and where to buy on Amazon and Flipkart.";
const URL = "https://rajshamani.life/raj-shamani-books";
const OG = OG_IMAGES.book;

const faq = [
  {
    q: "How many books has Raj Shamani written?",
    a: "Raj Shamani has written one book so far — Build Don't Talk, published in 2022. It became a national bestseller in India.",
  },
  {
    q: "What is Build Don't Talk about?",
    a: "Build Don't Talk is a practical guide to money, communication, leverage and mindset for young Indians. It argues that output beats announcement, and that skills like earning, speaking and network-building can be learned deliberately.",
  },
  {
    q: "Where can I buy Raj Shamani's book in India?",
    a: "Build Don't Talk is available on Amazon India and Flipkart in paperback, and as an ebook and audiobook on major platforms.",
  },
  {
    q: "Is Build Don't Talk worth reading?",
    a: "It is written for students, early-career professionals and first-time founders who want direct, India-first advice instead of generic motivation. Readers looking for advanced business theory will find it introductory by design.",
  },
  {
    q: "Who published Build Don't Talk?",
    a: "Build Don't Talk was published by Invincible Publishers in September 2022, ISBN 9789356291317.",
  },
];

export const Route = createFileRoute("/raj-shamani-books")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "website", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Build Don't Talk by Raj Shamani", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          about: { "@type": "Person", name: "Raj Shamani", url: "https://rajshamani.life/about" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          mainEntity: {
            "@type": "ItemList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Build Don't Talk",
                url: "https://rajshamani.life/build-dont-talk",
                item: {
                  "@type": "Book",
                  name: "Build Don't Talk",
                  author: { "@type": "Person", name: "Raj Shamani" },
                  isbn: "9789356291317",
                  bookFormat: "https://schema.org/Paperback",
                  inLanguage: "en",
                  datePublished: "2022-09-01",
                  publisher: { "@type": "Organization", name: "Invincible Publishers" },
                  url: "https://rajshamani.life/build-dont-talk",
                },
              },
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: BooksLanding,
});

function BooksLanding() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Books" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <BookOpen size={14} /> Books
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani Books — <span className="text-gradient-gold">the complete list</span>
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Raj Shamani has written one book to date: <em>Build Don't Talk</em>,
            a 2022 bestseller on money, communication and leverage for young
            India. This page covers what the book contains, who should read it,
            what it costs, and where to buy it — plus what's coming next.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <div className="p-8 rounded-xl bg-card border border-border">
          <div className="text-xs uppercase tracking-widest text-gold mb-3">2022 · Non-fiction</div>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Build Don't Talk</h2>
          <p className="text-muted-foreground leading-relaxed">
            Subtitled <em>Things You Wish You Were Taught at School</em>, the book
            distils a decade of running a family manufacturing business, building
            a personal brand from zero, angel investing, and interviewing India's
            best-known founders. It is organised around four ideas: money is a
            learnable skill, communication compounds faster than any other
            ability, leverage beats hours, and shipping beats announcing.
          </p>
          <dl className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div className="flex justify-between border-b border-border/50 py-2">
              <dt className="text-muted-foreground">Publisher</dt>
              <dd>Invincible Publishers</dd>
            </div>
            <div className="flex justify-between border-b border-border/50 py-2">
              <dt className="text-muted-foreground">Published</dt>
              <dd>September 2022</dd>
            </div>
            <div className="flex justify-between border-b border-border/50 py-2">
              <dt className="text-muted-foreground">ISBN</dt>
              <dd>9789356291317</dd>
            </div>
            <div className="flex justify-between border-b border-border/50 py-2">
              <dt className="text-muted-foreground">Formats</dt>
              <dd>Paperback, ebook, audiobook</dd>
            </div>
          </dl>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="https://www.amazon.in/Build-Dont-Talk-Things-Taught/dp/0143459686"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-gold text-primary-foreground font-medium hover:opacity-90 transition"
            >
              Buy on Amazon <ArrowRight size={16} />
            </a>
            <a
              href="https://www.flipkart.com/build-don-t-talk/p/itm81394118c089f"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-border hover:border-gold/60 hover:text-gold transition"
            >
              Buy on Flipkart
            </a>
            <Link
              to="/build-dont-talk"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-gold/60 text-gold hover:bg-gold hover:text-primary-foreground transition"
            >
              Full summary & key lessons
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 pb-20 space-y-12">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            What is the Raj Shamani book about?
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            When people search for a Raj Shamani book they almost always mean{" "}
            <em>Build Don't Talk: Things You Wish You Were Taught at School</em>.
            The premise is simple and slightly confrontational: the Indian
            education system teaches you how to pass exams, not how to earn
            money, negotiate a raise, sell an idea, or build a reputation that
            works while you sleep. Raj wrote the book he wanted at nineteen,
            when he was selling polymer additives for the family business and
            realised that nothing he had studied prepared him for a room full
            of factory buyers.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            The book is deliberately short and blunt. Chapters run a few pages
            each, arguments are backed by things Raj actually did — cold
            outreach, unpaid speaking gigs, the first hundred podcast episodes
            nobody watched — rather than by imported American case studies. If
            you have listened to Figuring Out, the voice is identical: direct,
            impatient with excuses, and more interested in what you shipped
            this month than what you plan to launch next year.
          </p>
        </div>

        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Build Don't Talk summary — the four core ideas
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            <strong>Money is a skill, not a personality trait.</strong> The book
            opens by separating income from wealth. Earning more is a solvable
            problem — you either raise the value of your output or you raise the
            number of people it reaches. Most young professionals do neither and
            then blame the economy.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            <strong>Communication compounds faster than any other ability.</strong>{" "}
            Raj argues that the person who can explain an idea clearly gets paid
            more than the person who merely has it. Practical chapters cover
            structuring a pitch, handling silence in a negotiation, and writing
            messages that get replies from people who owe you nothing.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            <strong>Leverage beats hours.</strong> Media, code, capital and
            distribution do work you don't have to repeat. This is the section
            that explains why he built an audience before he built a brand, and
            why House of X exists at all.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            <strong>Build, don't talk.</strong> The title chapter is an attack on
            announcement culture — launching on LinkedIn before the product
            exists, collecting credentials instead of customers. Proof of work
            is the only currency that survives a bad year.
          </p>
        </div>

        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Who should read Build Don't Talk?
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            The book is written for students in their final years, early-career
            professionals in their first or second job, creators trying to turn
            attention into income, and first-time founders who have an idea but
            no commercial instinct yet. If you are in that group, you will
            probably finish it in two sittings and act on at least three things.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            It is honest to say who it is <em>not</em> for. Operators running
            companies at scale, MBAs looking for frameworks, or readers who
            already know Naval, Kiyosaki and Ravikant by heart will find much of
            it familiar. The value here is Indian context — salaries, family
            expectations, small-city constraints and the social cost of taking
            an unconventional path — which the imported canon simply doesn't
            address.
          </p>
        </div>

        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Price, formats and where to buy in India
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            <em>Build Don't Talk</em> is sold in paperback across Amazon India
            and Flipkart, typically in the ₹200–₹350 range depending on the
            running discount, and is also available as an ebook and as an
            audiobook narrated in Raj's own voice. Physical copies appear in
            Crossword and most large-format bookstores in metro cities. Prices
            shift often, so treat the retailer page as the source of truth
            rather than any figure quoted on a blog.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            For readers who want the argument before the purchase, the{" "}
            <Link to="/build-dont-talk" className="text-gold hover:underline">
              full chapter-by-chapter summary
            </Link>{" "}
            on this site covers the key lessons and includes a downloadable
            recap.
          </p>
        </div>

        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Is there a second Raj Shamani book?
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            As of now, <em>Build Don't Talk</em> remains the only book Raj has
            authored. He has spoken in interviews about writing a second one
            focused on business building rather than personal skill — closer to
            the ground reality of running Figuring Out Media, House of X and an
            angel portfolio — but nothing has been announced with a publisher or
            a date. Until then, the closest thing to new long-form material is
            the podcast archive, where the same ideas get tested against
            founders who disagree with them.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">

        <h2 className="text-3xl md:text-4xl font-bold mb-10">
          Raj Shamani books — frequently asked questions
        </h2>
        <div className="space-y-8">
          {faq.map((f) => (
            <div key={f.q} className="pb-8 border-b border-border/50 last:border-0">
              <h3 className="text-lg font-bold mb-2">{f.q}</h3>
              <p className="text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 flex flex-wrap gap-4 text-sm">
          <Link to="/about" className="text-gold hover:underline">About Raj Shamani</Link>
          <Link to="/raj-shamani-podcast" className="text-gold hover:underline">Raj Shamani podcast</Link>
          <Link to="/shamani-industries" className="text-gold hover:underline">Shamani Industries</Link>
          <Link to="/contact" className="text-gold hover:underline">Contact</Link>
        </div>
      </section>
    <AionCta />
    <RelatedKeywords currentPath="/raj-shamani-books" />
    <RelatedPages pages={["aion", "book", "about", "podcast", "ventures", "faq", "contact"]} />
      </div>
  );
}
